import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  Save,
  Download,
  Mail,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Copy,
  Check,
  Bell,
  Calendar,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  formatMoney,
  useAddInvoiceLine,
  useInvoice,
  useIssueInvoice,
  useRemoveInvoiceLine,
  useRevertInvoiceToIssued,
  useUpdateInvoiceDraft,
  useUpdateInvoiceLine,
  useVoidInvoice,
  useInvoiceEmails,
  useRenderInvoicePdf,
  useSendInvoiceEmail,
  useInvoiceReminders,
  useSendInvoiceReminder,
  useRecordPayment,
  type InvoiceLineRow,
} from '@/hooks/useInvoices';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { InvoiceStatusBadges } from './Invoices';
import { AppointmentLinePicker } from '@/components/invoices/AppointmentLinePicker';
import { PaymentManagement } from '@/components/invoices/PaymentManagement';
import { usePatient } from '@/hooks/usePatients';
import {
  useCreatePayCheckout,
  useRequestClinioPayLink,
  useRequestRefund,
  useClinicPayStatus,
  deriveClinicPayDisplayStatus,
} from '@/hooks/useClinicPay';
import { supabase } from '@/lib/supabase';
import {
  useClinicSdEnabled,
  useValidateInvoice,
  useSubmitInvoice,
} from '@/hooks/useSygeforsikring';

interface LineDraft {
  description: string;
  quantity: number;
  unit_price_cents: number;
  vat_rate_percent: number;
  discount_pct: number;
}

const EMPTY_LINE: LineDraft = {
  description: '',
  quantity: 1,
  unit_price_cents: 0,
  vat_rate_percent: 25,
  discount_pct: 0,
};

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: invoice, isLoading, error } = useInvoice(id);
  const updateDraft = useUpdateInvoiceDraft();
  const addLine = useAddInvoiceLine();
  const updateLine = useUpdateInvoiceLine();
  const removeLine = useRemoveInvoiceLine();
  const issue = useIssueInvoice();
  const recordPayment = useRecordPayment();
  const revert = useRevertInvoiceToIssued();
  const voidInv = useVoidInvoice();
  const renderPdf = useRenderInvoicePdf();
  const sendEmail = useSendInvoiceEmail();
  const { data: emailHistory } = useInvoiceEmails(id);
  const { data: reminderHistory } = useInvoiceReminders(id);
  const sendReminder = useSendInvoiceReminder();
  const createCheckout = useCreatePayCheckout();
  const requestPayLink = useRequestClinioPayLink();
  const { data: payStatus } = useClinicPayStatus(activeClinicId);

  // SD: load clinic SD config + mutations
  const { data: sdInfo } = useClinicSdEnabled(activeClinicId);
  const validateInvoice = useValidateInvoice();
  const submitInvoice = useSubmitInvoice();
  const [showSdModal, setShowSdModal] = useState(false);
  const [sdValidation, setSdValidation] = useState<{
    valid: boolean;
    errors: Array<{ field?: string; message?: string }>;
  } | null>(null);

  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');
  const [showAddLine, setShowAddLine] = useState(false);
  const [showAppointmentPicker, setShowAppointmentPicker] = useState(false);
  const [newLine, setNewLine] = useState<LineDraft>(EMPTY_LINE);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLine, setEditLine] = useState<LineDraft>(EMPTY_LINE);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');

  const requestRefund = useRequestRefund();

  // Stripe pay state
  const [showPayLinkModal, setShowPayLinkModal] = useState(false);
  const [payLinkUrl, setPayLinkUrl] = useState('');
  const [payLinkCopied, setPayLinkCopied] = useState(false);
  const [showReminderHistory, setShowReminderHistory] = useState(false);

  // Clinio Pay refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmountDkk, setRefundAmountDkk] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');

  const canAdmin = useMemo(
    () => Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId))),
    [activeClinicId, isPlatformAdmin, isClinicAdmin],
  );

  const isPayActive = deriveClinicPayDisplayStatus(payStatus) === 'active';

  useEffect(() => {
    if (!invoice) return;
    setDueAt(invoice.due_at ? invoice.due_at.slice(0, 10) : '');
    setNotes(invoice.notes ?? '');
  }, [invoice]);

  // If patient paid online but sync was missed, pick it up when the clinic opens the invoice.
  useEffect(() => {
    const payToken = (invoice as { pay_token?: string | null } | undefined)?.pay_token;
    if (!invoice || invoice.status !== 'issued' || !payToken || payStatus?.status !== 'active') {
      return;
    }

    const invoiceId = invoice.id;

    let cancelled = false;
    async function syncPayStatus() {
      try {
        const { data, error } = await supabase.functions.invoke('pay-checkout-status', {
          body: { pay_token: payToken },
        });
        if (cancelled || error) return;

        const result = data as { ok?: boolean; data?: { status?: string } } | null;
        if (result?.ok && result.data?.status === 'succeeded') {
          void qc.invalidateQueries({ queryKey: ['invoices', invoiceId] });
        }
      } catch {
        // Non-fatal — clinic can refresh later
      }
    }

    void syncPayStatus();
    return () => {
      cancelled = true;
    };
  }, [invoice, payStatus?.status, qc]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('invoices.load_error')} />
      </div>
    );
  }

  const isDraft = invoice.status === 'draft';
  const isIssued = invoice.status === 'issued';
  const isPaid = invoice.status === 'paid';
  const isVoid = invoice.status === 'void';
  const patientSnap = (invoice.patient_snapshot ?? null) as null | {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
    address?: Record<string, string | null> | null;
    pat_number?: string | null;
  };
  const clinicSnap = (invoice.clinic_snapshot ?? null) as null | {
    name?: string;
    cvr?: string | null;
    invoice_prefix?: string;
    bank_info?: Record<string, unknown> | null;
  };

  async function handleDownloadPdf() {
    if (!invoice) return;
    try {
      const result = await renderPdf.mutateAsync(invoice.id);
      window.open(result.signed_url, '_blank');
    } catch (err) {
      console.error('render pdf failed', err);
      toast.error(t('invoices.pdf_error'));
    }
  }

  async function handlePayNow() {
    if (!invoice) return;
    try {
      const result = await createCheckout.mutateAsync({ invoice_id: invoice.id });
      window.location.href = result.checkout_url;
    } catch (err) {
      console.error('pay now failed', err);
      toast.error(t('payments.pay_error'));
    }
  }

  async function handleRequestPayLink() {
    if (!invoice) return;
    try {
      const result = await requestPayLink.mutateAsync(invoice.id);
      setPayLinkUrl(result.pay_url ?? `${window.location.origin}/pay-invoice/${result.pay_token}`);
      setPayLinkCopied(false);
      setShowPayLinkModal(true);
    } catch (err) {
      console.error('request pay link failed', err);
      toast.error(t('payments.pay_error'));
    }
  }

  async function handleRefund(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    const amountOre = Math.round(parseFloat(refundAmountDkk) * 100);
    if (!Number.isFinite(amountOre) || amountOre <= 0) {
      toast.error(t('payments.refund_invalid_amount'));
      return;
    }
    try {
      await requestRefund.mutateAsync({
        invoice_id: invoice.id,
        amount:     amountOre,
        reason:     refundReason,
      });
      toast.success(t('payments.refund_success'));
      setShowRefundModal(false);
      setRefundAmountDkk('');
    } catch (err) {
      console.error('refund failed', err);
      toast.error(t('payments.refund_error'));
    }
  }

  async function handleCopyPayLink() {
    try {
      await navigator.clipboard.writeText(payLinkUrl);
      setPayLinkCopied(true);
      setTimeout(() => setPayLinkCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  }

  function openSendEmailModal() {
    setEmailRecipient(patientSnap?.email ?? '');
    setEmailSubject(`Faktura ${invoice?.invoice_number ?? ''} fra ${clinicSnap?.name ?? ''}`);
    setEmailMessage('');
    setShowSendEmailModal(true);
  }

  async function handleSendEmail(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await sendEmail.mutateAsync({
        invoice_id: invoice.id,
        recipient: emailRecipient.trim() || undefined,
        subject: emailSubject.trim() || undefined,
        message: emailMessage.trim() || undefined,
      });
      toast.success(t('invoices.email_sent_toast'));
      setShowSendEmailModal(false);
    } catch (err) {
      console.error('send email failed', err);
      toast.error(t('invoices.email_failed_toast'));
    }
  }

  async function handleSendReminder(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await sendReminder.mutateAsync({ 
        invoice_id: invoice.id,
        custom_message: reminderMessage.trim() || undefined,
      });
      toast.success(t('invoices.reminder_sent_toast'));
      setShowReminderModal(false);
      setReminderMessage('');
    } catch (err) {
      console.error('send reminder failed', err);
      toast.error(t('invoices.reminder_failed_toast'));
    }
  }

  function openSendReminderModal() {
    setReminderMessage('');
    setShowReminderModal(true);
  }

  async function handleSaveDraft(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await updateDraft.mutateAsync({
        invoice_id: invoice.id,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('invoices.toast_saved'));
    } catch (err) {
      console.error('update draft failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleAddLine(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await addLine.mutateAsync({
        invoice_id: invoice.id,
        description: newLine.description.trim(),
        quantity: newLine.quantity,
        unit_price_cents: newLine.unit_price_cents,
        vat_rate_percent: newLine.vat_rate_percent,
        discount_pct: newLine.discount_pct || undefined,
      });
      toast.success(t('invoices.toast_line_added'));
      setNewLine(EMPTY_LINE);
      setShowAddLine(false);
    } catch (err) {
      console.error('add line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  function startEditLine(line: InvoiceLineRow) {
    setEditingLineId(line.id);
    setEditLine({
      description: line.description,
      quantity: Number(line.quantity),
      unit_price_cents: line.unit_price_cents,
      vat_rate_percent: Number(line.vat_rate_percent),
      discount_pct: line.discount_pct ? Number(line.discount_pct) : 0,
    });
  }

  async function handleUpdateLine(lineId: string, e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await updateLine.mutateAsync({
        line_id: lineId,
        invoice_id: invoice.id,
        description: editLine.description.trim(),
        quantity: editLine.quantity,
        unit_price_cents: editLine.unit_price_cents,
        vat_rate_percent: editLine.vat_rate_percent,
        discount_pct: editLine.discount_pct,
        clear_discount: editLine.discount_pct === 0,
      });
      toast.success(t('invoices.toast_line_updated'));
      setEditingLineId(null);
    } catch (err) {
      console.error('update line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleRemoveLine(line: InvoiceLineRow) {
    if (!invoice) return;
    if (!confirm(t('invoices.lines_remove') + '?')) return;
    try {
      await removeLine.mutateAsync({ line_id: line.id, invoice_id: invoice.id });
      toast.success(t('invoices.toast_line_removed'));
    } catch (err) {
      console.error('remove line failed', err);
      toast.error(t('invoices.save_error'));
    }
  }

  async function handleIssue() {
    if (!invoice) return;
    if (!confirm(t('invoices.confirm_issue'))) return;
    try {
      await issue.mutateAsync(invoice.id);
      toast.success(t('invoices.toast_issued'));
    } catch (err) {
      console.error('issue failed', err);
      toast.error(t('invoices.issue_error'));
    }
  }

  async function handleMarkPaid(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    
    // Record a payment for the full remaining balance
    const remainingBalance = invoice.total_cents - (invoice.total_paid_cents ?? 0);
    
    try {
      await recordPayment.mutateAsync({
        invoice_id: invoice.id,
        amount_cents: remainingBalance,
        payment_date: new Date().toISOString(),
        payment_method: paymentMethod.trim() || undefined,
        payment_reference: paymentReference.trim() || undefined,
        notes: paymentNotes.trim() || undefined,
      });
      toast.success(t('invoices.toast_paid'));
      setShowPaidModal(false);
      setPaymentMethod('');
      setPaymentReference('');
      setPaymentNotes('');
    } catch (err) {
      console.error('mark paid failed', err);
      toast.error(t('invoices.pay_error'));
    }
  }

  async function handleVoid(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await voidInv.mutateAsync({
        invoice_id: invoice.id,
        reason: voidReason.trim(),
      });
      toast.success(t('invoices.toast_voided'));
      setShowVoidModal(false);
      setVoidReason('');
    } catch (err) {
      console.error('void failed', err);
      toast.error(t('invoices.void_error'));
    }
  }

  async function handleRevert(e: FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    try {
      await revert.mutateAsync({
        invoice_id: invoice.id,
        reason: revertReason.trim(),
      });
      toast.success(t('invoices.toast_reverted'));
      setShowRevertModal(false);
      setRevertReason('');
    } catch (err) {
      console.error('revert failed', err);
      toast.error(t('invoices.revert_error'));
    }
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-4xl space-y-6 px-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/economy/invoices')}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('invoices.back_to_list')}
            </button>
            {invoice.patient_id && (
              <button
                type="button"
                onClick={() => navigate(`/patients/${invoice.patient_id}`)}
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('invoices.back_to_patient')}
              </button>
            )}
          </div>

          {/* Header card */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {invoice.invoice_number ?? t('invoices.draft_label')}
                  </h1>
                  <InvoiceStatusBadges status={invoice.status} lastSentAt={invoice.last_sent_at} lastReminderSentAt={invoice.last_reminder_sent_at} />
                </div>
                {patientSnap ? (
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {patientSnap.first_name} {patientSnap.last_name}
                    {patientSnap.pat_number && (
                      <span className="ml-2 font-mono text-xs">{patientSnap.pat_number}</span>
                    )}
                  </div>
                ) : (
                  <PatientFallback patientId={invoice.patient_id} />
                )}
                {invoice.issued_at && (
                  <div className="mt-1 text-xs text-gray-500">
                    {t('invoices.issued_at_label')}:{' '}
                    {new Date(invoice.issued_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {invoice.last_sent_at && (
                  <div className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
                    {t('invoices.email_sent_at', {
                      date: new Date(invoice.last_sent_at).toLocaleString('da-DK'),
                    })}
                    {invoice.last_emailed_to && (
                      <>
                        {' '}
                        {t('invoices.email_sent_to', { email: invoice.last_emailed_to })}
                      </>
                    )}
                  </div>
                )}
                {invoice.paid_at && (
                  <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                    {t('invoices.paid_at_label')}:{' '}
                    {new Date(invoice.paid_at).toLocaleDateString('da-DK')}
                  </div>
                )}
                {invoice.voided_at && (
                  <div className="mt-1 text-xs text-red-700 dark:text-red-400">
                    {t('invoices.voided_at_label')}:{' '}
                    {new Date(invoice.voided_at).toLocaleDateString('da-DK')}
                    {invoice.voided_reason && ` — ${invoice.voided_reason}`}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {isDraft && (
                  <Button
                    onClick={handleIssue}
                    disabled={issue.isPending || invoice.lines.length === 0}
                  >
                    {issue.isPending ? t('common.saving') : t('invoices.actions_issue')}
                  </Button>
                )}
                {isIssued && (
                  <Button onClick={() => setShowPaidModal(true)}>
                    {t('invoices.actions_mark_paid')}
                  </Button>
                )}
                {(isDraft || isIssued) && canAdmin && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handlePayNow}
                      disabled={createCheckout.isPending}
                    >
                      {createCheckout.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      <span className="ml-1">{t('payments.pay_now')}</span>
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleRequestPayLink}
                      disabled={requestPayLink.isPending || !isPayActive}
                      title={
                        !isPayActive
                          ? t('payments.pay_not_configured')
                          : undefined
                      }
                    >
                      {requestPayLink.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : null}
                      <span>{t('payments.send_pay_link')}</span>
                    </Button>
                  </>
                )}
                {isPaid && canAdmin && (
                  <Button variant="secondary" onClick={() => setShowRevertModal(true)}>
                    {t('invoices.actions_revert')}
                  </Button>
                )}
                {isPaid && canAdmin && (invoice as { pay_token?: string | null }).pay_token && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRefundAmountDkk(
                        (invoice.total_cents / 100).toFixed(2),
                      );
                      setRefundReason('requested_by_customer');
                      setShowRefundModal(true);
                    }}
                    disabled={requestRefund.isPending}
                  >
                    {requestRefund.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    <span className={requestRefund.isPending ? 'ml-1' : ''}>
                      {t('payments.refund_action')}
                    </span>
                  </Button>
                )}
                {(isDraft || isIssued) && canAdmin && (
                  <Button variant="secondary" onClick={() => setShowVoidModal(true)}>
                    {t('invoices.actions_void')}
                  </Button>
                )}
                {!isDraft && (
                  <Button
                    variant="secondary"
                    onClick={handleDownloadPdf}
                    disabled={renderPdf.isPending}
                  >
                    {renderPdf.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span className="ml-1">{t('invoices.download_pdf')}</span>
                  </Button>
                )}
                {(isIssued || isPaid) && canAdmin && (
                  <>
                    <Button onClick={openSendEmailModal}>
                      <Mail className="h-4 w-4" />
                      <span className="ml-1">{t('invoices.send_email')}</span>
                    </Button>
                    {isIssued && (
                      <Button
                        variant="secondary"
                        onClick={openSendReminderModal}
                        disabled={sendReminder.isPending}
                      >
                        {sendReminder.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Bell className="h-4 w-4" />
                        )}
                        <span className="ml-1">{t('invoices.send_reminder')}</span>
                      </Button>
                    )}
                  </>
                )}
                {/* Submit to Sygeforsikring Danmark — only for issued invoices not yet submitted */}
                {isIssued && canAdmin && sdInfo?.enabled && !invoice.sd_uuid && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      if (!activeClinicId || !id) return;
                      setSdValidation(null);
                      setShowSdModal(true);
                      try {
                        const r = await validateInvoice.mutateAsync({
                          clinic_id: activeClinicId,
                          invoice_id: id,
                        });
                        setSdValidation({ valid: r.valid, errors: r.errors });
                      } catch {
                        setSdValidation({
                          valid: false,
                          errors: [{ message: t('sd.validation_failed') }],
                        });
                      }
                    }}
                    disabled={validateInvoice.isPending}
                  >
                    {validateInvoice.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    <span className={validateInvoice.isPending ? 'ml-1' : ''}>
                      {t('sd.submit_to_sd')}
                    </span>
                  </Button>
                )}
                {/* Show SD UUID if already submitted */}
                {invoice.sd_uuid && (
                  <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-300">
                    SD: {invoice.sd_uuid}
                  </span>
                )}
                {clinicSnap && (
                  <div className="text-right text-xs text-gray-500">{clinicSnap.name}</div>
                )}
              </div>
            </div>
          </section>

          {/* Draft fields */}
          {isDraft && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              <form onSubmit={handleSaveDraft} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label={t('invoices.field_due_at')}
                    type="date"
                    value={dueAt}
                    onChange={(e) => setDueAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('invoices.field_notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={updateDraft.isPending}>
                    {updateDraft.isPending ? t('common.saving') : t('invoices.actions_save_draft')}
                  </Button>
                </div>
              </form>
            </section>
          )}

          {!isDraft && (invoice.due_at || invoice.notes) && (
            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
              {invoice.due_at && (
                <div className="text-sm">
                  <span className="text-gray-500">{t('invoices.field_due_at')}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(invoice.due_at).toLocaleDateString('da-DK')}
                  </span>
                </div>
              )}
              {invoice.notes && (
                <div className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                  {invoice.notes}
                </div>
              )}
            </section>
          )}

          {/* Lines */}
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('invoices.lines_title')}
              </h2>
              {isDraft && !showAddLine && (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setShowAppointmentPicker(true)}>
                    <Calendar className="mr-1 h-4 w-4" />
                    {t('invoices.appointments.add_from_appointment')}
                  </Button>
                  <Button size="sm" onClick={() => setShowAddLine(true)}>
                    <Plus className="mr-1 h-4 w-4" />
                    {t('invoices.lines_add')}
                  </Button>
                </div>
              )}
            </div>

            {invoice.lines.length === 0 && !showAddLine ? (
              <p className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {t('invoices.lines_empty')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <Th>{t('invoices.line_field_description')}</Th>
                      <Th>{t('invoices.line_field_quantity')}</Th>
                      <Th>{t('invoices.line_field_unit_price')}</Th>
                      <Th>{t('invoices.line_field_vat')}</Th>
                      <Th>{t('invoices.totals_total')}</Th>
                      <th className="relative px-3 py-3">
                        <span className="sr-only">{t('invoices.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {invoice.lines.map((line) =>
                      editingLineId === line.id ? (
                        <tr key={line.id}>
                          <td colSpan={6} className="p-3">
                            <form
                              onSubmit={(e) => handleUpdateLine(line.id, e)}
                              className="grid grid-cols-1 gap-3 md:grid-cols-6"
                            >
                              <Input
                                label={t('invoices.line_field_description')}
                                value={editLine.description}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, description: e.target.value })
                                }
                                className="md:col-span-2"
                                required
                              />
                              <Input
                                label={t('invoices.line_field_quantity')}
                                type="number"
                                step="0.01"
                                value={editLine.quantity.toString()}
                                onChange={(e) =>
                                  setEditLine({ ...editLine, quantity: Number(e.target.value) })
                                }
                              />
                              <Input
                                label={t('invoices.line_field_unit_price')}
                                type="number"
                                step="0.01"
                                value={(editLine.unit_price_cents / 100).toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    unit_price_cents: Math.round(Number(e.target.value) * 100),
                                  })
                                }
                              />
                              <Input
                                label={t('invoices.line_field_vat')}
                                type="number"
                                value={editLine.vat_rate_percent.toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    vat_rate_percent: Number(e.target.value),
                                  })
                                }
                              />
                              <Input
                                label={t('invoices.line_field_discount_pct')}
                                type="number"
                                value={editLine.discount_pct.toString()}
                                onChange={(e) =>
                                  setEditLine({
                                    ...editLine,
                                    discount_pct: Number(e.target.value),
                                  })
                                }
                              />
                              <div className="flex items-end gap-2 md:col-span-6">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setEditingLineId(null)}
                                >
                                  {t('common.cancel')}
                                </Button>
                                <Button type="submit" size="sm" disabled={updateLine.isPending}>
                                  <Save className="mr-1 h-4 w-4" />
                                  {t('invoices.lines_save')}
                                </Button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      ) : (
                        <tr key={line.id}>
                          <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                            <div>
                              {line.description}
                              {line.source === 'appointment' && line.source_id && (
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  {t('invoices.appointments.line_tag')}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {line.quantity}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {formatMoney(line.unit_price_cents, invoice.currency)}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {line.vat_rate_percent}%
                          </td>
                          <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {formatMoney(line.line_total_cents, invoice.currency)}
                          </td>
                          <td className="px-3 py-3 text-right text-sm">
                            {isDraft && (
                              <div className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEditLine(line)}
                                  className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                                  aria-label={t('common.edit')}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveLine(line)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400"
                                  aria-label={t('invoices.lines_remove')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {showAddLine && isDraft && (
              <form
                onSubmit={handleAddLine}
                className="mt-4 grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-6"
              >
                <Input
                  label={t('invoices.line_field_description')}
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  required
                  className="md:col-span-2"
                />
                <Input
                  label={t('invoices.line_field_quantity')}
                  type="number"
                  step="0.01"
                  value={newLine.quantity.toString()}
                  onChange={(e) => setNewLine({ ...newLine, quantity: Number(e.target.value) })}
                />
                <Input
                  label={t('invoices.line_field_unit_price')}
                  type="number"
                  step="0.01"
                  value={(newLine.unit_price_cents / 100).toString()}
                  onChange={(e) =>
                    setNewLine({
                      ...newLine,
                      unit_price_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <Input
                  label={t('invoices.line_field_vat')}
                  type="number"
                  value={newLine.vat_rate_percent.toString()}
                  onChange={(e) =>
                    setNewLine({ ...newLine, vat_rate_percent: Number(e.target.value) })
                  }
                />
                <Input
                  label={t('invoices.line_field_discount_pct')}
                  type="number"
                  value={newLine.discount_pct.toString()}
                  onChange={(e) => setNewLine({ ...newLine, discount_pct: Number(e.target.value) })}
                />
                <div className="flex items-end gap-2 md:col-span-6">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddLine(false);
                      setNewLine(EMPTY_LINE);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="sm" disabled={addLine.isPending}>
                    {addLine.isPending ? t('common.saving') : t('invoices.lines_add')}
                  </Button>
                </div>
              </form>
            )}

            <div className="ml-auto mt-6 max-w-xs space-y-1 text-sm">
              <Row label={t('invoices.totals_subtotal')}>
                {formatMoney(invoice.subtotal_cents, invoice.currency)}
              </Row>
              {invoice.discount_cents > 0 && (
                <Row label={t('invoices.totals_discount')}>
                  −{formatMoney(invoice.discount_cents, invoice.currency)}
                </Row>
              )}
              <Row label={t('invoices.totals_vat')}>
                {formatMoney(invoice.vat_cents, invoice.currency)}
              </Row>
              <Row label={t('invoices.totals_total')} bold>
                {formatMoney(invoice.total_cents, invoice.currency)}
              </Row>
            </div>
          </section>

          {/* Payment Management - shows for issued/partial/overdue/paid invoices */}
          {!isDraft && !isVoid && (
            <PaymentManagement invoice={invoice} canManage={canAdmin} />
          )}

          {/* Modals */}
          {showPaidModal && (
            <Modal onClose={() => setShowPaidModal(false)} title={t('invoices.actions_mark_paid')}>
              <form onSubmit={handleMarkPaid} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('invoices.mark_paid_description', 'Mark this invoice as fully paid. For partial payments or detailed tracking, use the Payment Management section below.')}
                </p>
                <Input
                  label={t('invoices.field_payment_method')}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  placeholder={t('invoices.payment_method_placeholder', 'e.g., Kontant, Bankoverførsel, MobilePay')}
                />
                <Input
                  label={t('invoices.field_payment_reference')}
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder={t('invoices.payment_reference_placeholder', 'e.g., Transaction ID')}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('invoices.payment_notes', 'Notes')} ({t('common.optional', 'Optional')})
                  </label>
                  <textarea
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder={t('invoices.payment_notes_placeholder', 'Additional notes about this payment')}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setShowPaidModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={recordPayment.isPending}>
                    {recordPayment.isPending ? t('common.saving') : t('invoices.actions_mark_paid')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showVoidModal && (
            <Modal onClose={() => setShowVoidModal(false)} title={t('invoices.actions_void')}>
              <form onSubmit={handleVoid} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('invoices.confirm_void')}
                </p>
                <Input
                  label={t('invoices.field_void_reason')}
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  required
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setShowVoidModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={voidInv.isPending || !voidReason.trim()}>
                    {voidInv.isPending ? t('common.saving') : t('invoices.actions_void')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showRevertModal && (
            <Modal onClose={() => setShowRevertModal(false)} title={t('invoices.actions_revert')}>
              <form onSubmit={handleRevert} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('invoices.confirm_revert')}
                </p>
                <Input
                  label={t('invoices.field_revert_reason')}
                  value={revertReason}
                  onChange={(e) => setRevertReason(e.target.value)}
                  required
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRevertModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={revert.isPending || !revertReason.trim()}>
                    {revert.isPending ? t('common.saving') : t('invoices.actions_revert')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showRefundModal && (
            <Modal onClose={() => setShowRefundModal(false)} title={t('payments.refund_title')}>
              <form onSubmit={handleRefund} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('payments.refund_desc')}
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('payments.refund_amount_label')}
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={(invoice.total_cents / 100).toFixed(2)}
                      value={refundAmountDkk}
                      onChange={(e) => setRefundAmountDkk(e.target.value)}
                      required
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                      DKK
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('payments.refund_reason_label')}
                  </label>
                  <select
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="requested_by_customer">{t('payments.refund_reason_customer')}</option>
                    <option value="duplicate">{t('payments.refund_reason_duplicate')}</option>
                    <option value="fraudulent">{t('payments.refund_reason_fraudulent')}</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowRefundModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={requestRefund.isPending || !refundAmountDkk}
                  >
                    {requestRefund.isPending ? t('common.saving') : t('payments.refund_action')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showSendEmailModal && (
            <Modal onClose={() => setShowSendEmailModal(false)} title={t('invoices.send_email')}>
              <form onSubmit={handleSendEmail} className="space-y-4">
                <Input
                  label={t('invoices.email_recipient_label')}
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  required
                />
                <Input
                  label={t('invoices.email_subject_label')}
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('invoices.email_message_label')}
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowSendEmailModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={sendEmail.isPending}>
                    {sendEmail.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    <span className="ml-1">
                      {sendEmail.isPending ? t('common.saving') : t('invoices.send_email')}
                    </span>
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showReminderModal && (
            <Modal onClose={() => setShowReminderModal(false)} title={t('invoices.send_reminder')}>
              <form onSubmit={handleSendReminder} className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('invoices.reminder_modal_desc')}
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('invoices.reminder_custom_message_label')}
                  </label>
                  <textarea
                    value={reminderMessage}
                    onChange={(e) => setReminderMessage(e.target.value)}
                    rows={4}
                    placeholder={t('invoices.reminder_custom_message_placeholder')}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('invoices.reminder_custom_message_hint')}
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowReminderModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={sendReminder.isPending}>
                    {sendReminder.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    <span className="ml-1">
                      {sendReminder.isPending ? t('common.saving') : t('invoices.send_reminder')}
                    </span>
                  </Button>
                </div>
              </form>
            </Modal>
          )}

          {showPayLinkModal && (
            <Modal onClose={() => setShowPayLinkModal(false)} title={t('payments.send_pay_link')}>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('payments.pay_link_desc')}
                </p>
                <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
                  <span className="flex-1 truncate text-sm text-gray-900 dark:text-white">
                    {payLinkUrl}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyPayLink}
                    className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {payLinkCopied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {payLinkCopied && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {t('payments.link_copied')}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={() => setShowPayLinkModal(false)}>
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {/* Sygeforsikring Danmark submit modal */}
          {showSdModal && id && activeClinicId && (
            <Modal
              title={t('sd.submit_modal_title')}
              onClose={() => {
                setShowSdModal(false);
                setSdValidation(null);
              }}
            >
              <div className="space-y-4">
                {validateInvoice.isPending && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sd.validating')}
                  </div>
                )}
                {sdValidation && (
                  <div
                    className={`rounded-lg p-4 text-sm ${sdValidation.valid ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}
                  >
                    <p
                      className={`font-medium ${sdValidation.valid ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}
                    >
                      {sdValidation.valid ? t('sd.validation_ok') : t('sd.validation_errors')}
                    </p>
                    {sdValidation.errors.length > 0 && (
                      <ul className="mt-2 list-inside list-disc space-y-1 text-red-700 dark:text-red-400">
                        {sdValidation.errors.map((e, i) => (
                          <li key={i}>
                            {e.field ? `${e.field}: ` : ''}
                            {e.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowSdModal(false);
                      setSdValidation(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    disabled={!sdValidation?.valid || submitInvoice.isPending}
                    onClick={async () => {
                      try {
                        const r = await submitInvoice.mutateAsync({
                          clinic_id: activeClinicId,
                          invoice_id: id,
                        });
                        toast.success(t('sd.submit_success', { uuid: r.uuid ?? '' }));
                        setShowSdModal(false);
                        setSdValidation(null);
                      } catch {
                        toast.error(t('sd.submit_error'));
                      }
                    }}
                  >
                    {submitInvoice.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t('sd.confirm_submit')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {!isDraft && (
            <>
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left"
                  onClick={() => setShowEmailHistory((v) => !v)}
                >
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('invoices.email_history')}
                    {emailHistory && emailHistory.length > 0 && (
                      <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        {emailHistory.length}
                      </span>
                    )}
                  </span>
                  {showEmailHistory ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {showEmailHistory && (
                  <div className="border-t border-gray-200 px-6 pb-4 dark:border-gray-700">
                    {!emailHistory || emailHistory.length === 0 ? (
                      <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('invoices.no_emails_yet')}
                      </p>
                    ) : (
                      <table className="mt-2 min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100 dark:border-gray-700">
                            <Th>{t('invoices.field_issue_date', 'Date')}</Th>
                            <Th>{t('invoices.email_recipient_label')}</Th>
                            <Th>{t('invoices.col_status', 'Status')}</Th>
                            <Th>Message-ID</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {emailHistory.map((row) => (
                            <tr key={row.id}>
                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">
                                {new Date(row.created_at).toLocaleString('da-DK')}
                              </td>
                              <td className="py-2 pr-4 text-gray-900 dark:text-white">
                                {row.sent_to}
                              </td>
                              <td className="py-2 pr-4">
                                <span
                                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                                    row.status === 'sent'
                                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className="py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                                {row.postmark_message_id ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left"
                  onClick={() => setShowReminderHistory((v) => !v)}
                >
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('invoices.reminder_history')}
                    {reminderHistory && reminderHistory.length > 0 && (
                      <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                        {reminderHistory.length}
                      </span>
                    )}
                  </span>
                  {showReminderHistory ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {showReminderHistory && (
                  <div className="border-t border-gray-200 px-6 pb-4 dark:border-gray-700">
                    {!reminderHistory || reminderHistory.length === 0 ? (
                      <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('invoices.no_reminders_yet')}
                      </p>
                    ) : (
                      <table className="mt-2 min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100 dark:border-gray-700">
                            <Th>{t('invoices.field_issue_date', 'Date')}</Th>
                            <Th>{t('invoices.reminder_number_label')}</Th>
                            <Th>{t('invoices.email_recipient_label')}</Th>
                            <Th>{t('invoices.days_overdue_label')}</Th>
                            <Th>{t('invoices.col_status', 'Status')}</Th>
                            <Th>{t('invoices.reminder_type_label')}</Th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {reminderHistory.map((row) => (
                            <tr key={row.id}>
                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">
                                {new Date(row.created_at).toLocaleString('da-DK')}
                              </td>
                              <td className="py-2 pr-4 text-gray-900 dark:text-white">
                                #{row.reminder_number}
                              </td>
                              <td className="py-2 pr-4 text-gray-900 dark:text-white">
                                {row.sent_to}
                              </td>
                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">
                                {row.days_overdue} {t('invoices.days_label')}
                              </td>
                              <td className="py-2 pr-4">
                                <span
                                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                                    row.status === 'sent'
                                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
                                      : row.status === 'failed'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                  }`}
                                >
                                  {row.status}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">
                                {row.is_manual ? t('invoices.manual_label') : t('invoices.automatic_label')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {isVoid && invoice.voided_reason && (
            <p className="text-sm text-red-700 dark:text-red-400">
              {t('invoices.void_reason_label')}: {invoice.voided_reason}
            </p>
          )}
        </div>
      </main>

      {showAppointmentPicker && isDraft && (
        <AppointmentLinePicker
          invoiceId={invoice.id}
          patientId={invoice.patient_id}
          clinicId={invoice.clinic_id}
          currency={invoice.currency}
          onClose={() => setShowAppointmentPicker(false)}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
    >
      {children}
    </th>
  );
}

function Row({
  label,
  bold,
  children,
}: {
  label: string;
  bold?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        bold ? 'border-t border-gray-200 pt-1 font-semibold dark:border-gray-700' : ''
      }`}
    >
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-gray-900 dark:text-white">{children}</span>
    </div>
  );
}

function PatientFallback({ patientId }: { patientId: string }) {
  const { data: patient } = usePatient(patientId);
  if (!patient) return null;
  return (
    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
      {patient.first_name} {patient.last_name}
      {patient.pat_number && <span className="ml-2 font-mono text-xs">{patient.pat_number}</span>}
    </div>
  );
}
